import { useEffect, useRef, useState } from 'react'

const INDUSTRY_LINKS = [
    { key: 'construction', label: 'Construction', href: '/industries/construction' },
    { key: 'events', label: 'Events', href: '/industries/events' },
    { key: 'tv-film', label: 'TV & Film', href: '/industries/tv-film' },
    { key: 'facilities', label: 'Facilities', href: '/industries/facilities' },
]

// Same panel/option visual language as IndustryDropdown (molecules/IndustryDropdown)
// — border-2 grey-300, rounded-md, shadow-lg, grey-100 hover — reused rather
// than designed fresh. The trigger itself stays a plain nav link (matching
// its Pricing/Resources siblings), not the bordered pill IndustryDropdown
// uses inline in the picker phrase — that styling belongs to that context,
// not a nav bar item.
//
// Unlike IndustryDropdown (a listbox that fakes focus via aria-activedescendant
// to update in-page state), this is a menu of real links: arrow keys move
// real DOM focus between the anchors, so Enter/Space activate them for free
// via native browser behaviour. Escape closes and returns focus to the trigger.
export function NavIndustriesDropdown() {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])

    const open = () => setIsOpen(true)

    const close = () => {
        setIsOpen(false)
        triggerRef.current?.focus()
    }

    useEffect(() => {
        if (isOpen) {
            itemRefs.current[0]?.focus()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return

        function handlePointerDown(event: MouseEvent) {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handlePointerDown)
        return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [isOpen])

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            open()
        }
    }

    const handleItemKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>, index: number) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                itemRefs.current[Math.min(index + 1, INDUSTRY_LINKS.length - 1)]?.focus()
                break
            case 'ArrowUp':
                event.preventDefault()
                itemRefs.current[Math.max(index - 1, 0)]?.focus()
                break
            case 'Escape':
                event.preventDefault()
                close()
                break
            case 'Tab':
                setIsOpen(false)
                break
        }
    }

    return (
        <div ref={containerRef} className="relative inline-block">
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={() => (isOpen ? close() : open())}
                onKeyDown={handleTriggerKeyDown}
                className="inline-flex items-center gap-1 font-medium text-unimo-slate"
            >
                Industries
                <svg
                    aria-hidden="true"
                    className={`h-4 w-4 shrink-0 text-unimo-site-turquoise transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    viewBox="0 0 20 20"
                    fill="none"
                >
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {isOpen && (
                <ul role="menu" className="absolute left-0 z-10 mt-2 w-max min-w-full rounded-md border-2 border-unimo-site-grey-300 bg-white py-1 shadow-lg">
                    {INDUSTRY_LINKS.map((industry, index) => (
                        <li key={industry.key} role="none">
                            <a
                                ref={(el) => {
                                    itemRefs.current[index] = el
                                }}
                                href={industry.href}
                                role="menuitem"
                                onKeyDown={(event) => handleItemKeyDown(event, index)}
                                onClick={close}
                                className="block cursor-pointer whitespace-nowrap px-4 py-2 font-medium text-unimo-slate hover:bg-unimo-site-grey-100"
                            >
                                {industry.label}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default NavIndustriesDropdown
